import get from "lodash/get";
import isNil from "lodash/isNil";
import type { Process } from "@/repositories/process/types";
import type { RealmSliceState } from "@/store/realm-slice";
import { PROCESS, REALMS } from "../constants/process";

export type ProcessType = "parent" | "child" | "normal";

export function goToProcessDetail(process: Process, navigateTo: (path: string) => void) {
  const processId = get(process, "id");
  const ProcessHasObserverProperty = processHasObserverProperty(process);

  if (!isLaunchedProcess(process) && !ProcessHasObserverProperty) {
    return;
  }

  if (ProcessHasObserverProperty) {
    navigateTo(`/subprocess/${process.id}`);
    return;
  }

  navigateTo(`/process/${processId}`);
}

export function isClosedProcess(process: Process) {
  const processStatusName = get(process, "status.Name");

  return processStatusName === PROCESS.ProcessStatus.CLOSED.Name;
}

export function isEvbasicRealm(realm: RealmSliceState["realm"]) {
  return realm === REALMS.EVBASIC;
}

export function isEvcoreRealm(realm: RealmSliceState["realm"]) {
  return realm === REALMS.EVCORE;
}

export function isLaunchedProcess(process: Process) {
  const processStatusName = get(process, "status.Name");
  return processStatusName === PROCESS.ProcessStatus.LAUNCHED.Name;
}

export function isMandatoryProcess(process?: Process) {
  return Boolean(get(process, "mandatory"));
}

export function isPaymentTypeDeferredEvaluatedProcess(process?: Process) {
  const paymentTypeName = get(process, "paymentType.Name") || "";

  return paymentTypeName === PROCESS.ProcessPaymentType.DEFERRED_EVALUATED.Name;
}

export function isPaymentTypeFreemiumProcess(process?: Process) {
  const paymentTypeName = get(process, "paymentType.Name") || "";

  return paymentTypeName === PROCESS.ProcessPaymentType.FREEMIUM.Name;
}

export function isB2CPaymentType(paymentTypeName?: string | null) {
  if (!paymentTypeName) {
    return false;
  }

  return (
    paymentTypeName === PROCESS.ProcessPaymentType.FREEMIUM.Name ||
    paymentTypeName === PROCESS.ProcessPaymentType.CLIENT.Name
  );
}

export function isPublicProcess(process?: Process) {
  return Boolean(get(process, "public"));
}

function isPotentialCompanyPlanProcess(process?: Process) {
  const typeCompanyPlanName = get(process, "typeCompanyPlan.Name");

  return typeCompanyPlanName === PROCESS.TypeCompanyPlan.POTENTIAL.Name;
}

function isPublicPotentialProcess(process?: Process) {
  if (!isPublicProcess(process)) {
    return false;
  }

  return isPotentialCompanyPlanProcess(process);
}

export function getProcessReportSource(process?: Process) {
  if (isPublicPotentialProcess(process)) {
    return PROCESS.ProcessReportSource.POTENTIAL.Name;
  }

  if (isPublicProcess(process)) {
    return PROCESS.ProcessReportSource.FEEDBACK.Name;
  }

  return null;
}

export function isPersonStatusDisqualifiedProcess(process?: Process) {
  const personStatusName = get(process, "processPersonStatus.Name");

  return personStatusName === PROCESS.ProcessPersonStatus.DISQUALIFIED.Name;
}

export function isPersonStatusEndedProcess(process?: Process) {
  const personStatusName = get(process, "processPersonStatus.Name");

  return personStatusName === PROCESS.ProcessPersonStatus.ENDED.Name;
}

export function isWorkflowProcess(process?: Process) {
  return Boolean(get(process, "workflow"));
}

export function processHasObserverProperty(process: Process) {
  return Boolean(get(process, "observer"));
}

export function processHasSubprocessId(process: Process) {
  const subProcessId = get(process, "subprocessId");

  return !isNil(subProcessId);
}

export function sortProcessesByStartDate(processes: Process[]) {
  if (!processes) {
    return [];
  }
  return [...processes].sort((a, b) => {
    if (!a.startDate) {
      return -1;
    }
    const processAStartDate = get(a, "startDate") || 0;
    const processBStartDate = get(b, "startDate") || 0;
    return processBStartDate - processAStartDate;
  });
}

export function sortProcessesByEndDate(processes: Process[]) {
  if (!processes) {
    return [];
  }
  return [...processes].sort((a, b) => {
    const processAEndDate = get(a, "endDate") || 0;
    const processBEndDate = get(b, "endDate") || 0;
    return processBEndDate - processAEndDate;
  });
}

export function isViewableProcessReport(process: Process, hasExcludeMandatory: boolean): boolean {
  if (hasExcludeMandatory) {
    return isPublicProcess(process);
  }

  // Early return for deferred evaluated mandatory processes
  if (isPaymentTypeDeferredEvaluatedProcess(process) && isMandatoryProcess(process)) {
    return false;
  }

  // Process must be launched or closed
  if (!isLaunchedProcess(process) && !isClosedProcess(process)) {
    return false;
  }

  // Process person must have ended or been disqualified
  if (!isPersonStatusEndedProcess(process) && !isPersonStatusDisqualifiedProcess(process)) {
    return false;
  }

  // Process must have at least one valid access condition
  if (processHasSubprocessId(process)) {
    return true;
  }

  if (isPaymentTypeFreemiumProcess(process)) {
    return true;
  }

  if (isPublicProcess(process)) {
    return true;
  }

  if (isPublicPotentialProcess(process)) {
    return true;
  }

  if (isPaymentTypeDeferredEvaluatedProcess(process)) {
    return true;
  }

  // If none of the access conditions are met
  return false;
}

export function isProcessWithChildren(process?: Process) {
  const children = get(process, "children");
  return Array.isArray(children) && children.length > 0 && children.some((child) => !isNil(child));
}

/**
 * Determines the type of a process based on its properties
 * @param process - The process detail object
 * @returns The process type: "parent", "child", or "normal"
 */
export function getProcessType(process: Process): ProcessType {
  // Parent process: has children
  if (process.children && process.children.length > 0) {
    return "parent";
  }

  // Child process: has a parentId
  if (process.parentId) {
    return "child";
  }

  // Normal process: no children and no parentId
  return "normal";
}

/**
 * Type guard to check if a process is a parent process
 * @param process - The process to check
 * @returns True if the process is a parent process
 */
export function isParentProcess(process: Process): boolean {
  return getProcessType(process) === "parent";
}

/**
 * Type guard to check if a process is a child process
 * @param process - The process to check
 * @returns True if the process is a child process
 */
export function isChildProcess(process: Process): boolean {
  return getProcessType(process) === "child";
}

/**
 * Calculates the number of hours until a given deadline
 * @param deadLine - The deadline timestamp in milliseconds
 * @returns The number of hours until the deadline (positive)
 */
export function calculateHoursUntilDeadline(deadLine: number): number {
  const today = new Date();
  const deadline = new Date(deadLine);
  const diffTime = deadline.getTime() - today.getTime();
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  return Math.max(diffHours, 0);
}

/**
 * Sorts processes by deadline proximity, putting the ones closer to closing first
 * @param processes - Array of processes to sort
 * @returns A new array of processes sorted by deadline proximity (closest first)
 */
export function sortProcessesByDeadlineProximity(processes: Process[]): Process[] {
  if (!processes) {
    return [];
  }

  return [...processes].sort((a, b) => {
    // If process doesn't have endDate, put it at the end
    if (!a.endDate && !b.endDate) {
      return 0;
    }
    if (!a.endDate) {
      return 1;
    }
    if (!b.endDate) {
      return -1;
    }

    // Calculate hours until deadline for both processes
    const hoursUntilA = calculateHoursUntilDeadline(a.endDate);
    const hoursUntilB = calculateHoursUntilDeadline(b.endDate);

    // Sort by ascending hours (closest deadline first)
    return hoursUntilA - hoursUntilB;
  });
}
